import { useMemo } from 'react';
import { useI18n } from '../i18n/I18nProvider';

function safeText(s) {
  return String(s || '').trim();
}

function clampText(s, max = 120) {
  const t = safeText(s);
  if (!t) return '';
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1)).trim()}…`;
}

function initialFromName(name) {
  const t = safeText(name);
  if (!t) return 'B';
  return t.slice(0, 1).toUpperCase();
}

function Icon({ name }) {
  const n = String(name || '').trim();
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8 };
  if (n === 'heart') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M12 21s-7-4.35-9.2-8.4C1.3 9.1 3.2 6.5 6.1 6.1c1.7-.2 3.3.6 3.9 2 .6-1.4 2.2-2.2 3.9-2 2.9.4 4.8 3 3.3 6.5C19 16.65 12 21 12 21z" />
      </svg>
    );
  }
  if (n === 'comment') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      </svg>
    );
  }
  if (n === 'share') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M22 2L11 13" />
        <path d="M22 2l-7 20-4-9-9-4 20-7z" />
      </svg>
    );
  }
  if (n === 'bookmark') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M6 3h12v18l-6-3-6 3V3z" />
      </svg>
    );
  }
  if (n === 'close') {
    return (
      <svg aria-hidden="true" {...common}>
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
      </svg>
    );
  }
  return null;
}

export default function InstagramMockPreview({
  previewUrl,
  mediaType,
  brandName,
  avatarDataUrl,
  caption,
  variant, // profile | feed | story
  onMediaLoad,
  onMediaError,
}) {
  const { t } = useI18n();
  const url = safeText(previewUrl);
  const mt = safeText(mediaType) || 'image';
  const v = variant === 'story' ? 'story' : variant === 'feed' ? 'feed' : 'profile';

  const username = useMemo(() => {
    const n = safeText(brandName);
    if (!n) return 'brand';
    return n
      .toLowerCase()
      .replace(/[^\p{L}\p{N}_ ]/gu, '')
      .trim()
      .replace(/\s+/g, '_')
      .slice(0, 24) || 'brand';
  }, [brandName]);

  const captionText = useMemo(() => clampText(caption, 140), [caption]);

  if (!url) return null;

  return (
    <div className={`IGMock IGMock--${v}`}>
      {v === 'story' ? (
        <>
          <div className="IGStoryTop">
            <div className="IGStoryBars" aria-hidden="true">
              <span className="IGStoryBar" />
              <span className="IGStoryBar IGStoryBar--dim" />
              <span className="IGStoryBar IGStoryBar--dim" />
            </div>
            <div className="IGStoryHeader">
              <div className="IGAvatar">
                {avatarDataUrl ? <img className="IGAvatarImg" src={avatarDataUrl} alt="" /> : <span className="IGAvatarFallback">{initialFromName(username)}</span>}
              </div>
              <div className="IGStoryMeta">
                <div className="IGUsername">{username}</div>
                <div className="IGStoryTime">1h</div>
              </div>
              <button type="button" className="IGIconBtn" aria-label={t('common.close')} tabIndex={-1}>
                <Icon name="close" />
              </button>
            </div>
          </div>

          <div className="IGStoryMedia">
            {mt === 'video' ? (
              <video className="IGMedia" src={url} controls playsInline onLoadedData={onMediaLoad} onError={onMediaError} />
            ) : (
              <img className="IGMedia" src={url} alt={t('preview.generatedAlt')} onLoad={onMediaLoad} onError={onMediaError} />
            )}
          </div>

          <div className="IGStoryBottom" aria-hidden="true">
            <div className="IGStoryReply">Send message</div>
          </div>
        </>
      ) : v === 'feed' ? (
        <>
          <div className="IGFeedHeader">
            <div className="IGAvatar">
              {avatarDataUrl ? <img className="IGAvatarImg" src={avatarDataUrl} alt="" /> : <span className="IGAvatarFallback">{initialFromName(username)}</span>}
            </div>
            <div className="IGUsername">{username}</div>
            <div className="IGSpacer" />
            <div className="IGDots" aria-hidden="true">
              •••
            </div>
          </div>

          <div className="IGFeedMedia">
            {mt === 'video' ? (
              <video className="IGMedia" src={url} controls playsInline onLoadedData={onMediaLoad} onError={onMediaError} />
            ) : (
              <img className="IGMedia" src={url} alt={t('preview.generatedAlt')} onLoad={onMediaLoad} onError={onMediaError} />
            )}
          </div>

          <div className="IGFeedActions" aria-hidden="true">
            <div className="IGFeedActionsLeft">
              <span className="IGIcon">
                <Icon name="heart" />
              </span>
              <span className="IGIcon">
                <Icon name="comment" />
              </span>
              <span className="IGIcon">
                <Icon name="share" />
              </span>
            </div>
            <div className="IGFeedActionsRight">
              <span className="IGIcon">
                <Icon name="bookmark" />
              </span>
            </div>
          </div>

          <div className="IGFeedText">
            {captionText ? (
              <div className="IGCaption">
                <span className="IGCaptionUser">{username}</span> {captionText}
              </div>
            ) : (
              <div className="IGCaption IGCaption--dim">{t('preview.instagramHint')}</div>
            )}
            <div className="IGCommentsHint">View all comments</div>
          </div>
        </>
      ) : (
        <>
          <div className="IGProfileTop">
            <div className="IGProfileTopLeft">
              <div className="IGUsername">{username}</div>
            </div>
            <div className="IGProfileTopRight" aria-hidden="true">
              <span className="IGDots">•••</span>
            </div>
          </div>

          <div className="IGProfileHeader">
            <div className="IGAvatar IGAvatar--lg">
              {avatarDataUrl ? <img className="IGAvatarImg" src={avatarDataUrl} alt="" /> : <span className="IGAvatarFallback">{initialFromName(username)}</span>}
            </div>
            <div className="IGProfileStats" aria-hidden="true">
              <div className="IGStat">
                <div className="IGStatNum">12</div>
                <div className="IGStatLabel">posts</div>
              </div>
              <div className="IGStat">
                <div className="IGStatNum">2.3k</div>
                <div className="IGStatLabel">followers</div>
              </div>
              <div className="IGStat">
                <div className="IGStatNum">143</div>
                <div className="IGStatLabel">following</div>
              </div>
            </div>
          </div>

          <div className="IGProfileBio">
            <div className="IGProfileName">{safeText(brandName) || username}</div>
            <div className={`IGProfileBioText ${captionText ? '' : 'IGCaption--dim'}`}>{captionText || t('preview.instagramHint')}</div>
          </div>

          <div className="IGHighlights" aria-hidden="true">
            <div className="IGHl">
              <div className="IGHlCircle" />
              <div className="IGHlLabel">Menu</div>
            </div>
            <div className="IGHl">
              <div className="IGHlCircle" />
              <div className="IGHlLabel">Offers</div>
            </div>
            <div className="IGHl">
              <div className="IGHlCircle" />
              <div className="IGHlLabel">Reviews</div>
            </div>
            <div className="IGHl">
              <div className="IGHlCircle" />
              <div className="IGHlLabel">Info</div>
            </div>
          </div>

          <div className="IGProfileTabs" aria-hidden="true">
            <span className="IGTab IGTab--active">▦</span>
            <span className="IGTab">▶</span>
            <span className="IGTab">▢</span>
          </div>

          <div className="IGGrid">
            <div className="IGGridItem IGGridItem--main">
              {mt === 'video' ? (
                <video className="IGGridMedia" src={url} muted playsInline loop onLoadedData={onMediaLoad} onError={onMediaError} />
              ) : (
                <img className="IGGridMedia" src={url} alt={t('preview.generatedAlt')} onLoad={onMediaLoad} onError={onMediaError} />
              )}
              {mt === 'video' ? <div className="IGGridBadge" aria-hidden="true">▶</div> : null}
            </div>
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="IGGridItem IGGridItem--ph" aria-hidden="true" />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
